import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import auth from './auth';
import logging from './logging';
import { AutoInvestmentsModel } from './models/AutoInvestmentsModel';
import { BankAccountsModel } from './models/BankAccountsModel';
import { ConsolidatedModel } from './models/ConsolidatedModel';
import { InboxModel } from './models/InboxModel';
import { InvestorAccountModel } from './models/InvestorAccountModel';
import { InvestorRolesModel } from './models/InvestorRoles';
import { MyProfileModel } from './models/MyProfileModel';
import { PropertiesModel } from './models/PropertiesModel';
import { RewardsModel } from './models/RewardsModel';
import { SubscriptionModel } from './models/SubscriptionModel';
import { TradesModel } from './models/TradesModel';
import { TransactionsModel } from './models/TransactionsModel';
import security from './security';
import support from './support';
import tracking from './tracking';

export type FeatureFlags = 'development' | 'staging' | 'production';

type Session = {
  email?: string;
  phone?: string;
  investorId: string;
  name: string;
  avatar?: string;
};

async function clear() {
  // close any open services relying on the session
  support.close();

  // delete the session
  await end();

  // sign out of cognito
  await auth.signOut();

  // dump any cached data that could interfere with the next session
  AutoInvestmentsModel.persist.clearStorage();
  BankAccountsModel.persist.clearStorage();
  ConsolidatedModel.persist.clearStorage();
  InboxModel.persist.clearStorage();
  InvestorAccountModel.persist.clearStorage();
  InvestorRolesModel.persist.clearStorage();
  MyProfileModel.persist.clearStorage();
  PropertiesModel.persist.clearStorage();
  RewardsModel.persist.clearStorage();
  SubscriptionModel.persist.clearStorage();
  TradesModel.persist.clearStorage();
  TransactionsModel.persist.clearStorage();
}

async function end() {
  localStorage.removeItem('session');
  tracking.reset();
  security.identify({ userId: '' });
}

function get() {
  const session = localStorage.getItem('session');
  if (session) {
    return JSON.parse(session) as Session;
  } else {
    return null;
  }
}

function hasSession() {
  const session = get();
  return session !== null;
}

async function start(investorId: string): Promise<{ success: boolean }> {
  try {
    const amplifyAuthSession = await fetchAuthSession();
    const cognitoUser = amplifyAuthSession.tokens?.idToken?.payload;
    if (!cognitoUser?.email) throw new Error('No cognito user found');

    // start session
    const session: Session = {
      email: cognitoUser?.email as string | undefined,
      investorId,
      name: (cognitoUser?.given_name || cognitoUser?.name) as string,
      phone: cognitoUser?.phone_number as string | undefined,
    };

    localStorage.setItem('session', JSON.stringify(session));

    // start services
    await ConsolidatedModel.getState().fetchData();
    await logging.start();
    await tracking.start();
    await support.start();
    await security.start();

    // log sign in
    tracking.track({ action: 'User Signed In' });

    return { success: true };
  } catch (err: any) {
    logging.logError(err);
    return { success: false };
  }
}

async function checkAuthenticated() {
  try {
    // no user, not authenticated
    try {
      await getCurrentUser();
    } catch (err) {
      return false;
    }

    // no session, not authenticated
    const currentSession = get();
    if (!currentSession) return false;

    let amplifyAuthSession;
    try {
      amplifyAuthSession = await fetchAuthSession();
    } catch (err) {
      return false;
    }
    const amplifyUserEmail = amplifyAuthSession.tokens?.idToken?.payload?.email;

    // fail-safe clean up in case somehow the emails do not match
    if (amplifyUserEmail !== currentSession.email) {
      return false;
    }

    // you got here, you're authenticated
    return true;
  } catch {
    return false;
  }
}

let hasInitialized = false;
export default (() => {
  if (!hasInitialized) {
    hasInitialized = true;
  }
  return {
    checkAuthenticated,
    clear,
    end,
    get,
    hasSession,
    start,
  };
})();
